import { Check, Refresh, Settings } from '@tempo-io/sui-icons';
import { Switch } from '@tempo-io/sui-switch';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isDevToolsEnabled } from '@/common/utils/dev-tools/isDevToolsEnabled';
import { resolveRunPresentation } from '@/features/agentic-insights/components/data/runLifecycle';
import type { RunSummary } from '@/features/agentic-insights/components/data/types';
import { formatRunMoment } from '@/features/agentic-insights/components/experimental/shared/data/runMoment';
import { useDismissOnOutside } from '@/features/agentic-insights/components/experimental/shared/ui/useDismissOnOutside';
import {
  useOpenVariant,
  useVariantSections,
  type VariantId,
  type VariantRow,
} from '@/features/agentic-insights/components/experimental/variants/VariantPicker';

interface RunsCogProps {
  /** Which variant is being read — feeds the folded-in variant rows below. */
  variant: VariantId;
  org: string;
  loopId: number;
  /** Whether the reading is a single loop's or the merged All loops one. */
  isAggregate: boolean;
  /** The loop the run history and its own name belong to — meaningless while `isAggregate`. */
  focusedLoopName: string;
  runs: RunSummary[];
  selectedRunId: string | undefined;
  onSelectRun: (runId: string) => void;
  /** Whether the run action is legal here — `scope === navLoopId`, per the ticket. */
  isNavScope: boolean;
  offContextLoopName: string;
  navLoopName: string;
  /** Whether the nav loop has ever had a run — decides "Run again now" vs "Run the first briefing". */
  navHasRun: boolean;
  canRun: boolean;
  isRunning: boolean;
  onRun: () => void;
  sampleData: boolean;
  onSampleDataChange: (sample: boolean) => void;
  fieldKeys: boolean;
  onFieldKeysChange: (on: boolean) => void;
}

/**
 * The runs and the run action, which variant is being read, in one list behind one cogwheel — the
 * header's only cog now that this has merged with the standalone `VariantPicker`, which this doc
 * comment's own "one cog, not two" plan called for. Runs and the run action first, since
 * reading one is the more common reason to open this; which variant is being read next, since
 * switching design is a rarer, more deliberate action; a `Developer` section stays dev/stage only,
 * folded in last rather than living in a second cogwheel beside this one.
 *
 * The variant options are rendered here directly (`useVariantSections`/`useOpenVariant` from
 * `VariantPicker.tsx`), not as a mounted `<VariantPicker>`: that component is its own independent
 * disclosure widget with its own open state and its own absolutely-positioned popover, and nesting one
 * popover inside another that is already open does not render. `VariantPicker` itself is untouched and
 * still renders directly in every variant that has no cog to fold into.
 *
 * One row per line (Briefing / Intelligence / Classic), each numbered variant an independently
 * clickable chip inline within it, rather than one full-width row per historical variant — every
 * variant stays one click away, just laid out compactly. The exact date is a hover tooltip on each
 * chip rather than shown inline. Only `selected` distinguishes a chip visually — there is no separate
 * "newest" marker; every chip in a line reads the same otherwise.
 *
 * Rendered from `ExperimentalInsightsPage.tsx` as `runsCog`, handed to `NavigationBar` as part of its
 * `trailing` slot, next to List/Details, per the user's own layout call. It only renders when
 * `NavigationBar` does — unblocked, with a readable run — and is absent during a run-state screen
 * (loading, failed, no run). That trades away the NEX-18040 guarantee that
 * this cog stayed mounted through those screens as provenance about the loop; the run-state screens'
 * own primary action (e.g. "Run the first briefing") is what is left reachable there instead. A
 * deliberate, accepted tradeoff of this layout, not an oversight.
 */
export const RunsCog = ({
  variant,
  org,
  loopId,
  isAggregate,
  focusedLoopName,
  runs,
  selectedRunId,
  onSelectRun,
  isNavScope,
  offContextLoopName,
  navLoopName,
  navHasRun,
  canRun,
  isRunning,
  onRun,
  sampleData,
  onSampleDataChange,
  fieldKeys,
  onFieldKeysChange,
}: RunsCogProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();
  const variantSections = useVariantSections(variant);
  const openVariant = useOpenVariant(variant, org, loopId);

  useDismissOnOutside(open, containerRef, () => setOpen(false));

  const runDisabled = !isNavScope || isRunning;
  // Kept identical to the no-run state's own action label — same action, and two names for it
  // would be a real bug.
  const actionLabel = isRunning
    ? t(($) => $.loopAgenticInsights.experimental.header.running)
    : navHasRun
      ? t(($) => $.loopAgenticInsights.experimental.header.runAgainNow)
      : t(($) => $.loopAgenticInsights.experimental.states.noRun.action);

  const runRow = (run: RunSummary, index: number) => {
    const isSelected = (selectedRunId ?? runs[0]?.runId) === run.runId;
    const presentation = resolveRunPresentation(run, Date.now());
    return (
      <button
        key={run.runId}
        type="button"
        role="option"
        aria-selected={isSelected}
        data-testid="agentic-x-cog-run"
        onClick={() => {
          setOpen(false);
          onSelectRun(run.runId);
        }}
        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-neutral-subtle-hovered ${
          isSelected ? 'bg-bg-brand-subtler text-text-brand-primary' : ''
        }`}
      >
        <span className="truncate">{formatRunMoment(run.generatedAt)}</span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-text-neutral-tertiary">
          {index === 0 ? <span>{t(($) => $.loopAgenticInsights.runPicker.latest)}</span> : null}
          {presentation !== 'completed' ? (
            <span>{t(($) => $.loopAgenticInsights.runPicker.status[presentation])}</span>
          ) : null}
          {isSelected ? <Check className="size-4" aria-hidden="true" /> : null}
        </span>
      </button>
    );
  };

  // Not the shared `Tooltip`: that one is fixed at `w-64`, sized and positioned for a sentence-length
  // footnote beside a word (`findingMeta.tsx`'s confidence basis). Forcing a 2-character chip into
  // that shape anchors a 256px box off a tiny trigger — the oversized, disconnected look this was
  // built to fix. This one is auto-width to the date text and centered over its chip (`left-1/2
  // -translate-x-1/2`) rather than left-anchored, so it doesn't overhang the panel's own right edge
  // for a chip sitting near it.
  const variantChip = (row: VariantRow) => (
    <span key={row.id} className="group/date relative inline-flex">
      <button
        type="button"
        role="option"
        aria-selected={row.selected}
        aria-describedby={`${row.id}-date`}
        data-testid="agentic-x-cog-variant"
        onClick={() => {
          setOpen(false);
          openVariant(row.id);
        }}
        className={`rounded px-1.5 py-0.5 text-xs hover:bg-bg-neutral-subtle-hovered ${
          row.selected
            ? 'bg-bg-brand-subtler text-text-brand-primary'
            : 'text-text-neutral-secondary'
        }`}
      >
        {row.label}
      </button>
      <span
        role="tooltip"
        id={`${row.id}-date`}
        className="pointer-events-none invisible absolute bottom-full left-1/2 z-30 mb-1.5 -translate-x-1/2 rounded-md border border-border-neutral bg-bg-surface-overlay px-2 py-1 text-xs whitespace-nowrap text-text-neutral-primary opacity-0 shadow-lg transition-opacity group-focus-within/date:visible group-focus-within/date:opacity-100 group-hover/date:visible group-hover/date:opacity-100"
      >
        {row.detail}
      </span>
    </span>
  );

  const variantLineRow = (section: (typeof variantSections)[number]) => (
    <div key={section.line} className="flex items-center gap-2 px-3 py-1.5">
      <span className="w-24 shrink-0 truncate text-xs text-text-neutral-secondary">
        {section.sectionLabel}
      </span>
      <span className="flex flex-wrap items-center gap-1">{section.rows.map(variantChip)}</span>
    </div>
  );

  return (
    <span ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-label={t(($) => $.loopAgenticInsights.experimental.header.runsAndSettings)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid="agentic-x-cog"
        onClick={() => setOpen((current) => !current)}
        className={`flex size-6 items-center justify-center rounded transition-colors ${
          open || isRunning
            ? 'bg-bg-neutral-subtle text-text-neutral-primary'
            : 'text-text-neutral-secondary hover:bg-bg-neutral-subtle hover:text-text-neutral-primary'
        }`}
      >
        <Settings className={`size-4 ${isRunning ? 'animate-spin' : ''}`} aria-hidden="true" />
      </button>

      {open ? (
        <div
          id={panelId}
          data-testid="agentic-x-cog-panel"
          className="absolute top-full right-0 z-20 mt-2 flex w-80 flex-col overflow-y-auto rounded-md border border-border-neutral bg-bg-surface-overlay py-1 shadow-lg"
        >
          {isAggregate ? (
            <>
              <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-text-neutral-secondary">
                {t(($) => $.loopAgenticInsights.experimental.header.runsHeading)}
              </span>
              <p className="px-3 py-1.5 text-xs leading-relaxed text-text-neutral-secondary">
                {t(($) => $.loopAgenticInsights.experimental.header.runsAggregateNote)}
              </p>
            </>
          ) : (
            <>
              <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-text-neutral-secondary">
                {t(($) => $.loopAgenticInsights.experimental.header.runsHeadingForLoop, {
                  loop: focusedLoopName,
                })}
              </span>
              {runs.length === 0 ? (
                <p className="px-3 py-1.5 text-xs leading-relaxed text-text-neutral-secondary">
                  {t(($) => $.loopAgenticInsights.experimental.states.noRun.cogNote)}
                </p>
              ) : (
                runs.map(runRow)
              )}
            </>
          )}

          {canRun ? (
            <>
              <span className="my-1 h-px bg-border-neutral-subtle" aria-hidden="true" />

              <button
                type="button"
                disabled={runDisabled}
                data-testid="agentic-x-run-action"
                onClick={() => {
                  setOpen(false);
                  onRun();
                }}
                className="flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-text-brand-primary hover:bg-bg-neutral-subtle-hovered disabled:cursor-not-allowed disabled:text-text-neutral-tertiary disabled:hover:bg-transparent"
              >
                <Refresh
                  className={`size-4 ${isRunning ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                {actionLabel}
              </button>
              {isNavScope ? null : (
                <p className="px-3 py-1.5 text-xs leading-relaxed text-text-neutral-secondary">
                  {isAggregate
                    ? t(($) => $.loopAgenticInsights.experimental.header.runOffContextAggregate, {
                        loop: navLoopName,
                      })
                    : t(($) => $.loopAgenticInsights.experimental.header.runOffContextLoop, {
                        reading: offContextLoopName,
                        loop: navLoopName,
                      })}
                </p>
              )}
            </>
          ) : null}

          <span className="my-1 h-px bg-border-neutral-subtle" aria-hidden="true" />
          <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-text-neutral-secondary">
            {t(($) => $.loopAgenticInsights.experimental.header.variantHeading)}
          </span>
          {variantSections.map(variantLineRow)}

          {isDevToolsEnabled() ? (
            <>
              <span className="my-1 h-px bg-border-neutral-subtle" aria-hidden="true" />
              <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-text-neutral-secondary">
                {t(($) => $.loopAgenticInsights.experimental.header.developerHeading)}
              </span>
              <span className="flex items-center justify-between gap-4 px-3 py-1.5 text-xs text-text-neutral-secondary">
                <span>{t(($) => $.loopAgenticInsights.experimental.header.useSampleData)}</span>
                <Switch
                  data-testid="agentic-x-sample-toggle"
                  checked={sampleData}
                  onCheckedChange={onSampleDataChange}
                />
              </span>
              <span className="flex items-center justify-between gap-4 px-3 py-1.5 text-xs text-text-neutral-secondary">
                <span>{t(($) => $.loopAgenticInsights.experimental.header.fieldKeys)}</span>
                <Switch
                  data-testid="agentic-x-field-keys-toggle"
                  checked={fieldKeys}
                  onCheckedChange={onFieldKeysChange}
                />
              </span>
            </>
          ) : null}
        </div>
      ) : null}
    </span>
  );
};
